import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { REPORT_CALLABLE, MARKETING_REPORT_ACTION } from './reportContract';

/** Mirrors reports/marketingReport.ts. */
export type MarketingMonthStatus = 'complete' | 'in_progress' | 'after_snapshot' | 'no_snapshot';
export interface MarketingMonth {
  month: string;
  channels: Array<{ channel: string; exGstCents: number; asEnteredCents: number; gst: 'inc' | 'ex' }>;
  spendExGstCents: number;
  leadsCreated: number | null;
  costPerLeadCents: number | null;
  status: MarketingMonthStatus;
}
export interface MarketingReportData {
  asAtDate: string | null;
  channels: string[];
  months: MarketingMonth[];
  missingCreatedDate: number;
  adSpendWarning?: string;
}
export type MarketingResult = { status: 'ok'; data: MarketingReportData };

/** `refreshKey` changes after a spend save, so the figures follow the entry without a reload. */
export function useMarketingReport(companyId: string | undefined, refreshKey = 0) {
  const [result, setResult] = useState<MarketingResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sequence = useRef(0);
  const load = useCallback(async () => {
    const request = ++sequence.current;
    if (!companyId) { setResult(null); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const call = httpsCallable(functions, REPORT_CALLABLE);
      const res = await call({ action: MARKETING_REPORT_ACTION, companyId });
      if (request === sequence.current) setResult(res.data as MarketingResult);
    } catch (err) {
      if (request === sequence.current) {
        setResult(null);
        const code = (err as { code?: string }).code;
        setError(code === 'functions/permission-denied' ? 'Company-wide access is required for this report.' : 'Marketing could not be loaded.');
      }
    } finally { if (request === sequence.current) setLoading(false); }
    // refreshKey is a deliberate dependency: it is how a save asks for a fresh read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, refreshKey]);
  // The counter is copied so the cleanup bumps the same ref object it was set up with.
  useEffect(() => { const seq = sequence; void load(); return () => { ++seq.current; }; }, [load]);
  return { result, loading, error, reload: load };
}
